import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, apiError, eur, itDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Plus, Pencil, Trash2, AlertTriangle, FileText, CalendarClock } from "lucide-react";

export default function ImpiantoDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [plant, setPlant] = useState(null);
  const [products, setProducts] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/plants/${id}`);
    setPlant(data);
    if (user?.role !== "agente") {
      const iv = await api.get("/interventions", { params: { customer_id: data.customer_id } });
      setInterventions(iv.data.filter((x) => x.plant_id === id));
    }
  }, [id, user?.role]);

  useEffect(() => {
    load();
    api.get("/products").then(({ data }) => setProducts(data));
  }, [load]);

  const fields = [
    { name: "label", label: "Descrizione componente", required: true, full: true },
    {
      name: "product_id", label: "Articolo", type: "select", full: true,
      options: products.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` })),
    },
    { name: "serial", label: "Matricola" },
    { name: "position", label: "Ubicazione" },
    { name: "quantity", label: "Quantità", type: "number" },
    { name: "rented", label: "A noleggio", type: "switch", hint: "Bene noleggiato al cliente" },
    { name: "last_check_at", label: "Ultima verifica", type: "date" },
    { name: "next_check_at", label: "Prossima verifica", type: "date" },
    { name: "notes", label: "Note", type: "textarea", full: true },
  ];

  const save = async (values) => {
    try {
      if (editing) await api.put(`/components/${editing.id}`, values);
      else await api.post(`/plants/${id}/components`, values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  if (!plant) return <p className="overline">Caricamento…</p>;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="impianto-detail-page">
      <PageHeader title={plant.name} subtitle={`${plant.customer_name} · ${plant.type}`}>
        {user?.role === "azienda" && (
          <>
            <Button asChild variant="outline" className="h-11 rounded-sm">
              <Link to={`/contratti?customer=${plant.customer_id}&plant=${id}`} data-testid="plant-new-contract">
                <FileText className="h-4 w-4 mr-2" /> Nuovo contratto
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-11 rounded-sm">
              <Link to={`/manutenzioni?plant=${id}`} data-testid="plant-new-maintenance">
                <CalendarClock className="h-4 w-4 mr-2" /> Nuova manutenzione
              </Link>
            </Button>
          </>
        )}
        {(user?.role === "azienda" || user?.role === "tecnico") && (
          <Button className="h-11 rounded-sm" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-component-btn">
            <Plus className="h-4 w-4 mr-2" /> Aggiungi componente
          </Button>
        )}
      </PageHeader>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <Card className="p-5 rounded-sm shadow-none">
          <p className="overline">Ubicazione</p>
          <p className="text-sm mt-2">{plant.address || "—"}</p>
          <p className="text-sm text-muted-foreground">{plant.city || "—"}</p>
        </Card>
        <Card className="p-5 rounded-sm shadow-none">
          <p className="overline">Installazione</p>
          <p className="font-display text-2xl font-black tracking-tighter mt-2">{itDate(plant.installed_at)}</p>
        </Card>
        <Card className="p-5 rounded-sm shadow-none">
          <p className="overline">Componenti</p>
          <p className="font-display text-2xl font-black tracking-tighter mt-2">{plant.components?.length || 0}</p>
        </Card>
      </div>

      <h3 className="font-display text-xl font-bold tracking-tight mb-3">Componenti installati</h3>
      <ListCard testid="componenti-list">
        {(plant.components || []).map((c) => {
          const overdue = c.next_check_at && c.next_check_at < today;
          return (
            <div key={c.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold">{c.label}</p>
                  {c.rented && <Badge variant="secondary" className="rounded-sm">noleggio</Badge>}
                  {overdue && (
                    <Badge className="rounded-sm bg-destructive text-destructive-foreground">
                      <AlertTriangle className="h-3 w-3 mr-1" /> verifica scaduta
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Q.tà {c.quantity} · matr. <span className="font-mono">{c.serial || "—"}</span> · {c.position || "—"} ·
                  ultima verifica {itDate(c.last_check_at)} · prossima {itDate(c.next_check_at)}
                </p>
              </div>
              {(user?.role === "azienda" || user?.role === "tecnico") && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="rounded-sm"
                          onClick={() => { setEditing(c); setOpen(true); }} data-testid={`edit-component-${c.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {user?.role === "azienda" && (
                    <Button variant="outline" size="sm" className="rounded-sm text-destructive"
                            onClick={async () => { await api.delete(`/components/${c.id}`); load(); }}
                            data-testid={`delete-component-${c.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {(plant.components || []).length === 0 && <Empty text="Nessun componente censito." />}
      </ListCard>

      {user?.role !== "agente" && (
      <>
      <h3 className="font-display text-xl font-bold tracking-tight mt-10 mb-3">Storico interventi impianto</h3>
      <ListCard testid="impianto-storico">
        {interventions.map((i) => (
          <div key={i.id} className="px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-sm">
                <span className="font-mono text-muted-foreground mr-2">{i.code}</span>
                {i.type}
              </p>
              <p className="text-xs text-muted-foreground">{itDate(i.scheduled_date)} · {i.technician_name || "—"}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-sm font-mono">{eur(i.totals?.total)}</span>
              <Badge variant={i.status === "completato" ? "secondary" : "default"} className="rounded-sm">{i.status}</Badge>
            </div>
          </div>
        ))}
        {interventions.length === 0 && <Empty text="Nessun intervento su questo impianto." />}
      </ListCard>
      </>
      )}

      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Modifica componente" : "Nuovo componente"}
                  fields={fields} initial={editing || { quantity: 1 }} onSubmit={save} testid="component-dialog" />
    </div>
  );
}
